import type { ScaleBand } from 'd3-scale';

import { isStacked } from './layout.js';
import { safeGet } from '../../core/utils/safe-get.js';
import {
  CategoricalBarData,
  Dimension,
  WeakMapCategory,
  WeakMapDimension,
} from '../types/categorical-bar-chart-data.js';
import {
  GroupMode,
  type CategoricalValueScales,
} from '../types/categorical-bar-chart.js';

export type BarPlotCoordinates = {
  valueCoord: (dimension: Dimension) => number;
  categoryCoord: (dimension: Dimension, category: CategoricalBarData) => number;
  barSize: (dimension: Dimension) => number;
  barThickness: (dimension: Dimension) => number;
};

/**
 * Get the top bar value coordinate
 * @param dimension - dimension
 * @param valueScale - value scale
 * @param dimensionMap -
 */
export function getTopValueCoord(
  dimension: Dimension,
  valueScale: CategoricalValueScales,
  dimensionMap: WeakMapDimension,
): number {
  const { value, cumulativeValue } = safeGet(dimensionMap, dimension);

  return valueScale((cumulativeValue ?? 0) + value) ?? 0;
}

/**
 * Get bar size base on valueScale
 *
 * @internal
 * @param value - value of current dimension
 * @param valueScale - value scale
 */
export function getBarSize(
  value: number,
  valueScale: CategoricalValueScales,
): number {
  const zeroCoordinate = valueScale(0) ?? 0;
  const scaledValue = valueScale(value) ?? 0;

  if (value >= 0) {
    return zeroCoordinate - scaledValue;
  }

  return scaledValue - zeroCoordinate;
}

/**
 * Get the value coord to print the bar
 * @internal
 * @param dimension - current dimension
 * @param valueScale - value scale
 * @param dimensionMap -
 */
function getValueCoord(
  dimension: Dimension,
  valueScale: CategoricalValueScales,
  dimensionMap: WeakMapDimension,
): number {
  const { value, cumulativeValue } = safeGet(dimensionMap, dimension);

  return (
    valueScale(
      value >= 0 ? (cumulativeValue ?? 0) + value : cumulativeValue ?? 0,
    ) ?? 0
  );
}

/**
 * Calculates the bar coordinate base on current dimension and the categoryScale. will consider if is Stacked or Grouped.
 * @param groupMode - group mode
 * @param categoryScale - category scale
 * @param valueScale - value scale
 * @param dimensionsScale - dimensions scale
 * @param isSingleDimension - is single dimension
 * @param dimensionMap - dimensions Map
 * @param categoryMap - category Map
 */
export function getBarCoordinates(
  groupMode: GroupMode,
  categoryScale: ScaleBand<string>,
  valueScale: CategoricalValueScales,
  dimensionsScale: ScaleBand<string>,
  isSingleDimension: boolean,
  dimensionMap: WeakMapDimension,
  categoryMap: WeakMapCategory,
): BarPlotCoordinates {
  if (isStacked(groupMode) || isSingleDimension) {
    return {
      categoryCoord: (dimension, category) =>
        categoryScale(safeGet(categoryMap, category).id) ?? 0,
      valueCoord: (dimension) =>
        getValueCoord(dimension, valueScale, dimensionMap),
      barThickness: () => categoryScale.bandwidth(),
      barSize: (dimension) =>
        getBarSize(safeGet(dimensionMap, dimension).value, valueScale),
    };
  }

  return {
    categoryCoord: (dimension, category) => {
      const categoryCoord =
        categoryScale(safeGet(categoryMap, category).id) ?? 0;
      const dimensionCoord =
        dimensionsScale(safeGet(dimensionMap, dimension).name) ?? 0;

      return categoryCoord + dimensionCoord;
    },
    valueCoord: (dimension) =>
      getValueCoord(dimension, valueScale, dimensionMap),
    barThickness: () => dimensionsScale.bandwidth(),
    barSize: (dimension) =>
      getBarSize(safeGet(dimensionMap, dimension).value, valueScale),
  };
}
